import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import { Download, FileText, Image, Settings } from 'lucide-react';
import SEO from '../../components/SEO';
import { useLanguage } from '../../i18n/LanguageContext';
import { downloadCategories as downloadCategoriesData } from '../../data/content';
import { trackEvent } from '../../analytics';

const iconMap: Record<string, any> = { FileText, Image, Settings };

const Downloads = () => {
  const { t, pick, path } = useLanguage();

  const downloadCategories = downloadCategoriesData.map((c: any) => ({ ...c, icon: iconMap[c.icon] }));

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    paddingTop: '4rem'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '2rem 1.5rem'
  };

  const headerStyle = {
    textAlign: 'center' as const,
    marginBottom: '4rem'
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1rem'
  };

  const highlightStyle = {
    color: '#dfa926'
  };

  const descriptionStyle = {
    fontSize: '1.25rem',
    color: '#d1d5db',
    maxWidth: '48rem',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(350px, 100%), 1fr))',
    gap: '2rem',
    marginBottom: '4rem'
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)',
    transition: 'all 0.3s ease'
  };

  const iconStyle = {
    width: '3rem',
    height: '3rem',
    color: '#dfa926',
    marginBottom: '1.5rem'
  };

  const cardTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'white',
    marginBottom: '1rem'
  };

  const cardDescStyle = {
    color: '#9ca3af',
    marginBottom: '1.5rem',
    lineHeight: '1.6'
  };

  const fileListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const fileItemStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem',
    marginBottom: '0.5rem',
    backgroundColor: 'rgba(58, 58, 58, 0.3)',
    borderRadius: '0.5rem',
    border: '1px solid rgba(75, 85, 99, 0.5)',
    transition: 'all 0.3s ease'
  };

  const fileInfoStyle = {
    flex: 1
  };

  const fileNameStyle = {
    color: 'white',
    fontWeight: '500',
    marginBottom: '0.25rem'
  };

  const fileMetaStyle = {
    color: '#9ca3af',
    fontSize: '0.875rem'
  };

  const downloadButtonStyle = {
    backgroundColor: '#dfa926',
    color: 'black',
    border: 'none',
    borderRadius: '0.375rem',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  return (
    <div style={pageStyle}>
      <SEO
        title={t('seo.downloads.title')}
        description={t('seo.downloads.description')}
        canonicalPath="/downloads"
        keywords={t('seo.downloads.keywords')}
      />
      <Navigation />
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            <span style={highlightStyle}>{t('downloads.titleHl')}</span> {t('downloads.titleRest')}
          </h1>
          <p style={descriptionStyle}>
            {t('downloads.desc')}
          </p>
        </div>

        <div style={gridStyle}>
          {downloadCategories.map((category, index) => {
            const IconComponent = category.icon;
            return (
              <div 
                key={index}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(58, 58, 58, 0.5)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconComponent style={iconStyle} />
                <h3 style={cardTitleStyle}>{pick(category, 'title')}</h3>
                <p style={cardDescStyle}>{pick(category, 'description')}</p>
                
                <ul style={fileListStyle}>
                  {category.files.map((file, fileIndex) => (
                    <li 
                      key={fileIndex} 
                      style={fileItemStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(75, 85, 99, 0.4)';
                        e.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(58, 58, 58, 0.3)';
                        e.currentTarget.style.borderColor = 'rgba(75, 85, 99, 0.5)';
                      }}
                    >
                      <div style={fileInfoStyle}>
                        <div style={fileNameStyle}>{pick(file, 'name')}</div>
                        <div style={fileMetaStyle}>{file.type} • {file.size}</div>
                      </div>
                      <button
                        style={downloadButtonStyle}
                        onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#c8941f'}
                        onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#dfa926'}
                        onClick={() => {
                          const fileName = pick(file, 'name');
                          trackEvent('technical_document_download', { document: fileName });
                          const subject = encodeURIComponent(`${t('downloads.docRequestSubject')}: ${fileName}`);
                          const body = encodeURIComponent(`${t('downloads.docRequestBody')} "${fileName}".\n\n${t('downloads.docRequestThanks')}`);
                          window.location.href = `mailto:info@hestiadoors.com?subject=${subject}&body=${body}`;
                        }}
                        title={`${t('downloads.request')} ${pick(file, 'name')}`}
                      >
                        <Download size={16} />
                        {t('downloads.request')}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(223, 169, 38, 0.1) 0%, rgba(223, 169, 38, 0.05) 100%)',
            padding: '3rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(223, 169, 38, 0.2)'
          }}>
            <h3 style={{ color: 'white', fontSize: '2rem', marginBottom: '1rem' }}>
              {t('downloads.ctaTitle')}
            </h3>
            <p style={{ color: '#d1d5db', marginBottom: '2rem', fontSize: '1.125rem' }}>
              {t('downloads.ctaDesc')}
            </p>
            <Link
              to={path('/contact')}
              style={{
                backgroundColor: '#dfa926',
                color: 'black',
                padding: '0.75rem 2rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#c8941f'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#dfa926'}
            >
              {t('downloads.contactSupport')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Downloads;
